import { useMemo, useState, type CSSProperties } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import HourglassEmpty from "@mui/icons-material/HourglassEmpty";
import { fetchPublicBookingConfig, publicBookingRequest } from "@/lib/queryClient";
import {
  apiPublicPath,
  coerceBookingApiEntityId,
  mapServiceResponse,
  mapStaffResponse,
  parseJsonBytesField,
  unwrapPagedList,
  type BookingApiService,
  type BookingApiStaff,
} from "@/lib/bookingApi";
import { useToast } from "@/hooks/useToast";
import { validateEmailRequired, validateRequiredName } from "@/lib/formValidation";
import ThemeToggle from "@/components/ThemeToggle";
import type { BookingPageConfig, Service, Staff } from "@shared/schema";
import { radiusToCss } from "@shared/schema";

function isoDateLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function startOfTodayLocal(): Date {
  const n = new Date();
  n.setHours(0, 0, 0, 0);
  return n;
}

export default function PublicWaitlistPage() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { slug: slugParam } = useParams<{ slug: string }>();
  const slug = slugParam?.trim() ?? "";

  const isPreview =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [staffId, setStaffId] = useState<"any" | string>("any");
  const [fieldErrors, setFieldErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    service?: string;
    date?: string;
  }>({});
  const [submitted, setSubmitted] = useState(false);

  const todayIso = useMemo(() => isoDateLocal(startOfTodayLocal()), []);
  const [preferredDate, setPreferredDate] = useState(todayIso);

  const {
    data: rawConfig,
    isPending: loadingConfig,
    isError: configError,
    error: configQueryError,
    refetch: refetchConfig,
  } = useQuery({
    queryKey: ["public-config-waitlist", slug, isPreview],
    queryFn: () => fetchPublicBookingConfig(slug, { preview: isPreview }),
    enabled: Boolean(slug),
  });

  const cfg = useMemo((): BookingPageConfig | null | undefined => {
    if (!rawConfig) return undefined;
    const draft = parseJsonBytesField<BookingPageConfig>(rawConfig.bookingPageDraftConfig);
    const live = parseJsonBytesField<BookingPageConfig>(rawConfig.bookingPageConfig);
    if (isPreview) return draft ?? live ?? null;
    return live ?? null;
  }, [rawConfig, isPreview]);

  const maxHorizonDays = rawConfig?.maxBookingHorizonDays ?? 90;
  const maxDateIso = useMemo(() => {
    const end = startOfTodayLocal();
    end.setDate(end.getDate() + maxHorizonDays);
    return isoDateLocal(end);
  }, [maxHorizonDays]);

  const themeStyle: CSSProperties = cfg
    ? ({
        "--brand-primary": cfg.primaryColor,
        "--brand-radius": radiusToCss(cfg.radius),
      } as CSSProperties)
    : {};

  /** Match booking page: brand colour when set, otherwise app primary (blue). */
  const primaryColor = cfg?.primaryColor ?? theme.palette.primary.main;

  const { data: services, isLoading: loadingServices } = useQuery<Service[]>({
    queryKey: ["public-waitlist-services", slug],
    queryFn: async () => {
      const res = await publicBookingRequest("GET", apiPublicPath(slug, "/services"));
      const arr = unwrapPagedList<BookingApiService>(await res.json());
      return arr.map(mapServiceResponse);
    },
    enabled: Boolean(slug),
  });

  /** Mock services may use opaque string ids (`svc-1`); numeric-only guard broke staff fetch + join. */
  const staffQueryEnabled = Boolean(slug) && serviceId !== "";

  const { data: staffRows, isLoading: loadingStaff } = useQuery<Staff[]>({
    queryKey: ["public-waitlist-staff", slug, serviceId],
    queryFn: async () => {
      const base = apiPublicPath(slug, "/staff");
      const path = `${base}?serviceId=${encodeURIComponent(serviceId)}`;
      const res = await publicBookingRequest("GET", path);
      const arr = unwrapPagedList<BookingApiStaff>(await res.json());
      return arr.map(mapStaffResponse);
    },
    enabled: staffQueryEnabled,
  });

  const activeServices = useMemo(() => services?.filter((s) => s.active) ?? [], [services]);

  const joinMutation = useMutation({
    mutationFn: async () => {
      const sid = coerceBookingApiEntityId(serviceId);
      const sidOk =
        (typeof sid === "number" && Number.isFinite(sid)) ||
        (typeof sid === "string" && sid.trim() !== "");
      if (!sidOk) {
        throw new Error(t("publicWaitlist.validationService"));
      }
      const body: Record<string, unknown> = {
        customerEmail: email.trim(),
        customerName: `${firstName} ${lastName}`.trim(),
        requestedDate: preferredDate,
        serviceId: sid,
      };
      const phoneTrim = phone.trim();
      if (phoneTrim) body.customerPhone = phoneTrim.slice(0, 32);
      if (staffId !== "any") {
        const st = coerceBookingApiEntityId(staffId);
        if (typeof st === "number" && Number.isFinite(st)) body.staffId = st;
      }
      const res = await publicBookingRequest("POST", apiPublicPath(slug, "/waitlist"), body);
      return res.json() as Promise<Record<string, unknown>>;
    },
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (err: Error) =>
      toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  function validate(): boolean {
    const next: typeof fieldErrors = {};
    const fn = validateRequiredName(firstName, t("bookingFlow.firstName"));
    if (fn) next.firstName = fn;
    const ln = validateRequiredName(lastName, t("bookingFlow.lastName"));
    if (ln) next.lastName = ln;
    const em = validateEmailRequired(email);
    if (em) next.email = em;
    if (!serviceId) next.service = t("publicWaitlist.validationService");
    if (!preferredDate || preferredDate < todayIso || preferredDate > maxDateIso) {
      next.date = t("publicWaitlist.validationDate");
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!slug) return;
    if (!validate()) return;
    joinMutation.mutate();
  }

  if (!slug) {
    return (
      <Box sx={{ p: 3, maxWidth: 480, mx: "auto" }}>
        <Typography color="error">{t("publicWaitlist.missingSlug")}</Typography>
      </Box>
    );
  }

  if (loadingConfig) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: 2 }}>
        <Skeleton variant="rounded" width="100%" height={48} sx={{ maxWidth: 480, mx: "auto", mb: 2 }} />
        <Skeleton variant="rounded" width="100%" height={320} sx={{ maxWidth: 480, mx: "auto" }} />
      </Box>
    );
  }

  if (configError || !rawConfig) {
    return (
      <Box sx={{ p: 3, maxWidth: 480, mx: "auto" }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {configQueryError instanceof Error ? configQueryError.message : t("publicWaitlist.loadError")}
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" onClick={() => refetchConfig()}>
            {t("actions.retry")}
          </Button>
          <Button component={RouterLink} to={`/book/${encodeURIComponent(slug)}`} variant="text">
            {t("publicWaitlist.backToBooking")}
          </Button>
        </Stack>
      </Box>
    );
  }

  const bookingPageHeading = cfg?.pageTitle?.trim() || rawConfig.name?.trim() || t("publicWaitlist.title");

  if (submitted) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }} style={themeStyle}>
        <Paper elevation={0} square sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
          <Box
            sx={{
              maxWidth: 672,
              mx: "auto",
              px: 2,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography fontWeight={600}>{bookingPageHeading}</Typography>
            {cfg?.themeMode === "auto" && <ThemeToggle />}
          </Box>
        </Paper>
        <Box sx={{ maxWidth: 448, mx: "auto", px: 2, py: 6, textAlign: "center" }}>
          <Typography variant="h5" fontWeight={700} gutterBottom data-testid="text-waitlist-success">
            {t("publicWaitlist.successTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("publicWaitlist.successBody", { email: email.trim() })}
          </Typography>
          <Button component={RouterLink} to={`/book/${encodeURIComponent(slug)}`} variant="contained" data-testid="button-back-to-booking">
            {t("publicWaitlist.backToBooking")}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }} style={themeStyle}>
      <Paper elevation={0} square sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper", position: "sticky", top: 0, zIndex: 50 }}>
        <Box
          sx={{
            maxWidth: 672,
            mx: "auto",
            px: 2,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Button component={RouterLink} to={`/book/${encodeURIComponent(slug)}`} size="small" data-testid="button-waitlist-back">
              {t("publicWaitlist.backToBooking")}
            </Button>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: primaryColor,
                }}
              >
                <HourglassEmpty sx={{ fontSize: 18, color: "common.white" }} aria-hidden />
              </Box>
              <Typography fontWeight={600} data-testid="text-waitlist-title">
                {t("publicWaitlist.title")}
              </Typography>
            </Stack>
          </Stack>
          {cfg?.themeMode === "auto" && <ThemeToggle />}
        </Box>
      </Paper>

      {isPreview && (
        <Box
          sx={{
            bgcolor: "warning.light",
            color: "warning.dark",
            textAlign: "center",
            py: 1,
            typography: "body2",
            fontWeight: 600,
          }}
          data-testid="preview-banner-waitlist"
        >
          {t("bookingFlow.previewBanner")}
        </Box>
      )}

      <Box sx={{ maxWidth: 480, mx: "auto", px: 2, py: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("publicWaitlist.subtitle")}
        </Typography>

        <Card variant="outlined" component="form" onSubmit={handleSubmit}>
          <CardContent>
            <Stack spacing={2}>
              <TextField
                required
                label={t("bookingFlow.firstNameLabel")}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={Boolean(fieldErrors.firstName)}
                helperText={fieldErrors.firstName}
                inputProps={{ "data-testid": "input-waitlist-first-name" }}
                autoComplete="given-name"
              />
              <TextField
                required
                label={t("bookingFlow.lastNameLabel")}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={Boolean(fieldErrors.lastName)}
                helperText={fieldErrors.lastName}
                inputProps={{ "data-testid": "input-waitlist-last-name" }}
                autoComplete="family-name"
              />
              <TextField
                required
                type="email"
                label={t("bookingFlow.emailLabel")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
                inputProps={{ "data-testid": "input-waitlist-email" }}
                autoComplete="email"
              />
              <TextField
                label={t("bookingFlow.phoneLabel")}
                value={phone}
                onChange={(e) => setPhone(e.target.value.slice(0, 32))}
                inputProps={{ "data-testid": "input-waitlist-phone" }}
                autoComplete="tel"
              />
              <TextField
                id="select-waitlist-service"
                required
                select
                label={t("publicWaitlist.service")}
                value={serviceId}
                onChange={(e) => {
                  setServiceId(e.target.value);
                  setStaffId("any");
                }}
                error={Boolean(fieldErrors.service)}
                helperText={fieldErrors.service}
                disabled={loadingServices}
              >
                <MenuItem value="" disabled>
                  {loadingServices ? t("common.loading") : t("publicWaitlist.chooseService")}
                </MenuItem>
                {activeServices.map((s) => (
                  <MenuItem key={String(s.id)} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                id="select-waitlist-staff"
                select
                label={t("publicWaitlist.staffOptional")}
                value={staffId}
                onChange={(e) => setStaffId(e.target.value as "any" | string)}
                disabled={!serviceId || loadingStaff}
              >
                <MenuItem value="any">{t("bookingFlow.anyStaff")}</MenuItem>
                {(staffRows ?? []).map((s) => (
                  <MenuItem key={String(s.id)} value={String(s.id)}>
                    {s.displayName}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                required
                type="date"
                label={t("publicWaitlist.preferredDate")}
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                error={Boolean(fieldErrors.date)}
                helperText={fieldErrors.date}
                inputProps={{ min: todayIso, max: maxDateIso, "data-testid": "input-waitlist-date" }}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={joinMutation.isPending || loadingServices}
                data-testid="button-waitlist-submit"
              >
                {joinMutation.isPending ? t("publicWaitlist.submitting") : t("publicWaitlist.submit")}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
